import { Card, Form, InputNumber, Select, Typography } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import {
  canAddTriggerCondition,
  conditionTypeOptionsForRow,
  createEmptyTriggerCondition,
  intervalUnitsForConditionType,
  MAX_TRIGGER_CONDITIONS,
  type ConditionTypeOption,
  type TriggerConditionFormValue,
} from '~/app/components/maintenance/tasks-triggers/utils';

const { Paragraph } = Typography;

export interface TriggersConditionFormListProps {
  form: FormInstance;
  conditionTypeOptions: ConditionTypeOption[];
}

export function TriggersConditionFormList({
  form,
  conditionTypeOptions,
}: TriggersConditionFormListProps) {
  const { t } = useTranslation('maintenance');

  return (
    <>
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        {t('triggers.conditionsHint', { max: MAX_TRIGGER_CONDITIONS })}
      </Paragraph>

      <Form.List
        name="conditions"
        initialValue={[createEmptyTriggerCondition()]}
      >
        {(fields, { add, remove }) => (
          <>
            {fields.map((field) => (
              <Card
                key={field.key}
                size="small"
                style={{ marginBottom: 12 }}
                extra={
                  fields.length > 1 ? (
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  ) : null
                }
              >
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => {
                    const prevConditions = prev.conditions as
                      | TriggerConditionFormValue[]
                      | undefined;
                    const curConditions = cur.conditions as
                      | TriggerConditionFormValue[]
                      | undefined;
                    return (
                      JSON.stringify(prevConditions) !==
                      JSON.stringify(curConditions)
                    );
                  }}
                >
                  {({ getFieldValue }) => {
                    const conditions = getFieldValue('conditions') as
                      | TriggerConditionFormValue[]
                      | undefined;
                    const typeOptions = conditionTypeOptionsForRow(
                      conditionTypeOptions,
                      conditions,
                      field.name,
                    );
                    return (
                      <Form.Item
                        name={[field.name, 'conditionType']}
                        label={t('triggers.conditionType')}
                        rules={[{ required: true }]}
                      >
                        <Select
                          options={typeOptions}
                          onChange={() => {
                            form.setFieldValue(
                              ['conditions', field.name, 'intervalUnit'],
                              undefined,
                            );
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => {
                    const prevConditions = prev.conditions as
                      | TriggerConditionFormValue[]
                      | undefined;
                    const curConditions = cur.conditions as
                      | TriggerConditionFormValue[]
                      | undefined;
                    return (
                      prevConditions?.[field.name]?.conditionType !==
                      curConditions?.[field.name]?.conditionType
                    );
                  }}
                >
                  {({ getFieldValue }) => {
                    const conditions = getFieldValue('conditions') as
                      | TriggerConditionFormValue[]
                      | undefined;
                    const conditionType =
                      conditions?.[field.name]?.conditionType;
                    const unitOptions = intervalUnitsForConditionType(
                      conditionType,
                    ).map((v) => ({
                      label: t(`intervalUnit.${v}`),
                      value: v,
                    }));
                    return (
                      <Form.Item
                        name={[field.name, 'intervalUnit']}
                        label={t('triggers.intervalUnit')}
                        rules={[{ required: true }]}
                      >
                        <Select
                          options={unitOptions}
                          disabled={!conditionType}
                          placeholder={t('triggers.selectIntervalUnit')}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
                <Form.Item
                  name={[field.name, 'intervalValue']}
                  label={t('triggers.intervalValue')}
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Card>
            ))}
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const conditions = getFieldValue('conditions') as
                  | TriggerConditionFormValue[]
                  | undefined;
                const allowAdd = canAddTriggerCondition(conditions);
                return (
                  <Button
                    type="dashed"
                    onClick={() => add(createEmptyTriggerCondition())}
                    block
                    icon={<PlusOutlined />}
                    disabled={!allowAdd}
                  >
                    {allowAdd
                      ? t('triggers.addCondition')
                      : t('triggers.addConditionDisabled')}
                  </Button>
                );
              }}
            </Form.Item>
          </>
        )}
      </Form.List>
    </>
  );
}
